import {
  createAuthenticationError,
  resolveAuthenticationReasonFromState,
} from "../auth/auth-guidance";
import { touchDaemonCatalogSyncSignal } from "../local-service/state/catalog-sync-signal";
import { getAuthSessionState, type StoredAuthSession } from "./auth-session";

export type WakeLoopProfileKind = "human" | "agent" | "system";
export type WakeLoopProfileStatus = "active" | "disabled";
export type WakeLoopProfileManagedRole = "wakeloop_assistant";

export type WakeLoopProfile = {
  wakeLoopProfileId: string;
  profileName: string;
  profileHandle: string;
  profileKind: WakeLoopProfileKind;
  isDefault?: boolean;
  status: WakeLoopProfileStatus;
  ownerUserId: string;
  ownerName?: string;
  source: string;
  managedRole?: WakeLoopProfileManagedRole | null;
  createdByUserId: string | null;
  creatorProfileId?: string | null;
  lastUsedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

const VALIDATED_SESSION_CACHE_TTL_MS = 5000;
const DEFAULT_WAKELOOP_PROFILES_API_TIMEOUT_MS = 10_000;
const LOOPBACK_WAKELOOP_PROFILES_API_TIMEOUT_MS = 2500;
const PROFILE_HANDLE_PREFIX_RE = /^@+/;

let validatedSessionCache: {
  session: StoredAuthSession;
  validatedAtMs: number;
} | null = null;

export function resetWakeLoopProfilesSessionCacheForTests(): void {
  validatedSessionCache = null;
}

export function primeWakeLoopProfilesSessionCache(
  session: StoredAuthSession
): void {
  validatedSessionCache = {
    session,
    validatedAtMs: Date.now(),
  };
}

class AuthProfilesApiError extends Error {
  readonly status: number;
  readonly endpoint: string;
  readonly code: string | null;

  constructor(input: {
    message: string;
    status: number;
    endpoint: string;
    code?: string | null;
  }) {
    super(input.message);
    this.name = "AuthProfilesApiError";
    this.status = input.status;
    this.endpoint = input.endpoint;
    this.code = normalizeOptionalString(input.code);
  }
}

function isAuthProfilesApiError(error: unknown): error is AuthProfilesApiError {
  return error instanceof AuthProfilesApiError;
}

export async function listWakeLoopProfiles(): Promise<WakeLoopProfile[]> {
  const payload = await requestWakeLoopProfilesApi({
    method: "GET",
    path: "/v1/profiles",
  });

  const profiles = readArrayField(payload, "profiles");
  return profiles.map(parseWakeLoopProfileItem);
}

export async function resolveWakeLoopProfile(input: {
  wakeLoopProfileId?: string;
  profileKind?: WakeLoopProfileKind;
}): Promise<WakeLoopProfile> {
  const payload = await requestWakeLoopProfilesApi({
    method: "POST",
    path: "/v1/profiles/resolve",
    body: {
      ...(normalizeOptionalString(input.wakeLoopProfileId)
        ? {
            wakeLoopProfileId: normalizeOptionalString(input.wakeLoopProfileId),
          }
        : {}),
      ...(input.profileKind ? { profileKind: input.profileKind } : {}),
    },
  });

  return parseWakeLoopProfileItem(readRecordField(payload, "profile"));
}

export async function resolveWakeLoopProfileByIdOrName(input: {
  profileIdentifier: string;
  profileKind?: WakeLoopProfileKind;
}): Promise<WakeLoopProfile> {
  const profileIdentifier = normalizeOptionalString(input.profileIdentifier);
  if (!profileIdentifier) {
    throw new Error("profile identifier is required");
  }

  try {
    return await resolveWakeLoopProfile({
      wakeLoopProfileId: profileIdentifier,
      ...(input.profileKind ? { profileKind: input.profileKind } : {}),
    });
  } catch (idError) {
    const matched = await findWakeLoopProfileByNameOrHandle({
      profileIdentifier,
      ...(input.profileKind ? { profileKind: input.profileKind } : {}),
    });
    if (matched.status === "found") {
      return matched.profile;
    }
    if (matched.status === "ambiguous") {
      throw new Error(
        `WakeLoop profile name is ambiguous: ${profileIdentifier}. Pass the profile ID instead.`
      );
    }
    if (isAuthProfilesApiError(idError)) {
      throw new Error(`WakeLoop profile not found: ${profileIdentifier}`);
    }
    throw idError;
  }
}

export function createWakeLoopAgentProfile(input: {
  profileName: string;
  creatorProfileId?: string | null;
}): Promise<WakeLoopProfile> {
  const profileName = normalizeOptionalString(input.profileName);
  const creatorProfileId =
    normalizeOptionalString(input.creatorProfileId) ?? undefined;
  if (!profileName) {
    throw new Error("profile name is required");
  }
  return createWakeLoopAgentProfileRequest({
    profileName,
    ...(creatorProfileId ? { creatorProfileId } : {}),
  });
}

export function ensureWakeLoopManagedAgentProfile(input: {
  profileName: string;
  managedRole: WakeLoopProfileManagedRole;
  creatorProfileId?: string | null;
}): Promise<WakeLoopProfile> {
  const profileName = normalizeOptionalString(input.profileName);
  const creatorProfileId =
    normalizeOptionalString(input.creatorProfileId) ?? undefined;
  if (!profileName) {
    throw new Error("profile name is required");
  }
  return ensureWakeLoopManagedAgentProfileRequest({
    profileName,
    managedRole: input.managedRole,
    ...(creatorProfileId ? { creatorProfileId } : {}),
  });
}

export async function createWakeLoopHumanProfile(input: {
  profileName: string;
  creatorProfileId?: string | null;
}): Promise<WakeLoopProfile> {
  const profileName = normalizeOptionalString(input.profileName);
  const creatorProfileId =
    normalizeOptionalString(input.creatorProfileId) ?? undefined;
  if (!profileName) {
    throw new Error("profile name is required");
  }

  const payload = await requestWakeLoopProfilesApi({
    method: "POST",
    path: "/v1/profiles/humans",
    body: {
      profileName,
      ...(creatorProfileId ? { creatorProfileId } : {}),
    },
  });

  const created = parseWakeLoopProfileItem(readRecordField(payload, "profile"));
  await emitRouteCatalogSync("profile_created");
  return created;
}

export async function updateWakeLoopProfile(input: {
  wakeLoopProfileId: string;
  profileName?: string;
}): Promise<WakeLoopProfile> {
  const wakeLoopProfileId = normalizeOptionalString(input.wakeLoopProfileId);
  if (!wakeLoopProfileId) {
    throw new Error("wakeLoopProfileId is required");
  }

  const profileName = normalizeOptionalString(input.profileName);
  const hasProfileName = Boolean(profileName);

  if (!hasProfileName) {
    throw new Error("profileName is required");
  }

  const payload = await requestWakeLoopProfilesApi({
    method: "PATCH",
    path: `/v1/profiles/${encodeURIComponent(wakeLoopProfileId)}`,
    body: {
      ...(profileName ? { profileName } : {}),
    },
  });

  const updated = parseWakeLoopProfileItem(readRecordField(payload, "profile"));
  await emitRouteCatalogSync("profile_updated");
  return updated;
}

export async function deleteWakeLoopProfile(input: {
  wakeLoopProfileId: string;
}): Promise<WakeLoopProfile> {
  const wakeLoopProfileId = normalizeOptionalString(input.wakeLoopProfileId);
  if (!wakeLoopProfileId) {
    throw new Error("wakeLoopProfileId is required");
  }

  const payload = await requestWakeLoopProfilesApi({
    method: "DELETE",
    path: `/v1/profiles/${encodeURIComponent(wakeLoopProfileId)}`,
  });

  const deleted = parseWakeLoopProfileItem(readRecordField(payload, "profile"));
  await emitRouteCatalogSync("profile_deleted");
  return deleted;
}

async function requestWakeLoopProfilesApi(input: {
  method: "GET" | "POST" | "DELETE" | "PATCH";
  path: string;
  body?: Record<string, unknown>;
}): Promise<Record<string, unknown>> {
  const session = await requireWakeLoopProfilesSession();
  const endpoint = toAuthEndpoint(session.authBaseUrl, input.path);
  const response = await fetch(endpoint, {
    method: input.method,
    headers: {
      Authorization: `${normalizeTokenType(session.tokenType)} ${session.accessToken}`,
      ...(input.body ? { "content-type": "application/json" } : {}),
    },
    ...(input.body ? { body: JSON.stringify(input.body) } : {}),
    signal: AbortSignal.timeout(resolveWakeLoopProfilesApiTimeoutMs(endpoint)),
  });

  const payload = await parseResponseJson(response);

  if (!response.ok) {
    const errorRecord = isRecord(payload) ? payload : {};
    const status = response.status;
    const code = readOptionalStringField(errorRecord, "code");
    if (status === 401 || status === 403 || code === "auth.unauthenticated") {
      validatedSessionCache = null;
      throw createAuthenticationError({
        reason: "required",
      });
    }

    const message = resolveProfilesApiErrorMessage({
      payload,
      status,
      code,
      endpoint,
    });
    throw new AuthProfilesApiError({
      status,
      endpoint,
      code,
      message,
    });
  }

  const record = isRecord(payload) ? payload : {};
  const ok = readBooleanField(record, "ok");
  if (!ok) {
    const code = readOptionalStringField(record, "code");
    throw new AuthProfilesApiError({
      status: response.status,
      endpoint,
      code,
      message: resolveProfilesApiErrorMessage({
        payload: record,
        status: response.status,
        code,
        endpoint,
      }),
    });
  }

  return record;
}

async function createWakeLoopAgentProfileRequest(input: {
  profileName: string;
  creatorProfileId?: string;
}): Promise<WakeLoopProfile> {
  const payload = await requestWakeLoopProfilesApi({
    method: "POST",
    path: "/v1/profiles/agents",
    body: {
      profileName: input.profileName,
      ...(input.creatorProfileId
        ? { creatorProfileId: input.creatorProfileId }
        : {}),
    },
  });

  const created = parseWakeLoopProfileItem(readRecordField(payload, "profile"));
  await emitRouteCatalogSync("profile_created");
  return created;
}

async function ensureWakeLoopManagedAgentProfileRequest(input: {
  profileName: string;
  managedRole: WakeLoopProfileManagedRole;
  creatorProfileId?: string;
}): Promise<WakeLoopProfile> {
  const payload = await requestWakeLoopProfilesApi({
    method: "POST",
    path: "/v1/profiles/agents/managed/ensure",
    body: {
      profileName: input.profileName,
      managedRole: input.managedRole,
      ...(input.creatorProfileId
        ? { creatorProfileId: input.creatorProfileId }
        : {}),
    },
  });

  const ensured = parseWakeLoopProfileItem(readRecordField(payload, "profile"));
  await emitRouteCatalogSync("profile_ensured");
  return ensured;
}

async function requireWakeLoopProfilesSession(): Promise<StoredAuthSession> {
  const now = Date.now();
  if (
    validatedSessionCache &&
    now - validatedSessionCache.validatedAtMs <= VALIDATED_SESSION_CACHE_TTL_MS
  ) {
    const localState = await getAuthSessionState({
      validateRemote: false,
    });
    if (
      localState.state === "valid" &&
      localState.session &&
      isSameStoredAuthSession(localState.session, validatedSessionCache.session)
    ) {
      return validatedSessionCache.session;
    }
    validatedSessionCache = null;
  }

  const state = await getAuthSessionState({
    validateRemote: true,
  });

  switch (state.state) {
    case "valid": {
      if (!state.session) {
        throw new Error("auth session unavailable");
      }
      validatedSessionCache = {
        session: state.session,
        validatedAtMs: now,
      };
      return state.session;
    }
    case "missing":
      validatedSessionCache = null;
      throw createAuthenticationError({
        reason: "required",
      });
    case "expired":
    case "invalid":
    case "revoked":
      validatedSessionCache = null;
      throw createAuthenticationError({
        reason: resolveAuthenticationReasonFromState(state.state) ?? "required",
      });
    default:
      validatedSessionCache = null;
      throw createAuthenticationError({
        reason: "required",
      });
  }
}

function resolveWakeLoopProfilesApiTimeoutMs(endpoint: string): number {
  const url = parseUrlOrNull(endpoint);
  if (url && isLoopbackHostname(url.hostname)) {
    return LOOPBACK_WAKELOOP_PROFILES_API_TIMEOUT_MS;
  }
  return DEFAULT_WAKELOOP_PROFILES_API_TIMEOUT_MS;
}

function parseUrlOrNull(value: string): URL | null {
  try {
    return new URL(value);
  } catch {
    return null;
  }
}

function isLoopbackHostname(hostname: string): boolean {
  return (
    hostname === "localhost" || hostname === "127.0.0.1" || hostname === "::1"
  );
}

function isSameStoredAuthSession(
  a: StoredAuthSession,
  b: StoredAuthSession
): boolean {
  return (
    a.authBaseUrl === b.authBaseUrl &&
    a.clientId === b.clientId &&
    a.tokenType === b.tokenType &&
    a.accessToken === b.accessToken &&
    (a.expiresAt ?? null) === (b.expiresAt ?? null)
  );
}

async function emitRouteCatalogSync(reason: string): Promise<void> {
  await touchDaemonCatalogSyncSignal({ reason }).catch(() => undefined);
}

function parseWakeLoopProfileItem(value: unknown): WakeLoopProfile {
  const record = isRecord(value) ? value : {};

  const wakeLoopProfileId = readOptionalStringField(
    record,
    "wakeLoopProfileId"
  );
  if (!wakeLoopProfileId) {
    throw new Error("invalid profile payload: missing wakeLoopProfileId");
  }

  const profileName = readRequiredStringField(record, "profileName");
  const profileHandle = readRequiredStringField(record, "profileHandle");
  const profileKind = readProfileKind(record, "profileKind");
  const isDefault = readOptionalBooleanField(record, "isDefault") ?? false;
  const status = readProfileStatus(record, "status");
  const ownerUserId = readRequiredStringField(record, "ownerUserId");
  const ownerName = readOptionalStringField(record, "ownerName") ?? undefined;
  const source = readRequiredStringField(record, "source");
  const managedRole = readOptionalManagedRoleField(record, "managedRole");
  const createdByUserId =
    readOptionalStringField(record, "createdByUserId") ?? null;
  const creatorProfileId =
    readOptionalStringField(record, "creatorProfileId") ?? null;
  const lastUsedAt = readOptionalStringField(record, "lastUsedAt");
  const createdAt = readRequiredStringField(record, "createdAt");
  const updatedAt = readRequiredStringField(record, "updatedAt");

  return {
    wakeLoopProfileId,
    profileName,
    profileHandle,
    profileKind,
    isDefault,
    status,
    ownerUserId,
    ...(ownerName ? { ownerName } : {}),
    source,
    managedRole,
    createdByUserId,
    creatorProfileId,
    lastUsedAt,
    createdAt,
    updatedAt,
  };
}

function readProfileKind(
  input: Record<string, unknown>,
  field: string
): WakeLoopProfileKind {
  const value = readRequiredStringField(input, field);
  if (value === "human" || value === "agent" || value === "system") {
    return value;
  }
  throw new Error(`invalid profile payload: unsupported ${field}`);
}

function readProfileStatus(
  input: Record<string, unknown>,
  field: string
): WakeLoopProfileStatus {
  const value = readRequiredStringField(input, field);
  if (value === "active" || value === "disabled") {
    return value;
  }
  throw new Error(`invalid profile payload: unsupported ${field}`);
}

function readOptionalManagedRoleField(
  input: Record<string, unknown>,
  field: string
): WakeLoopProfileManagedRole | null {
  const value = readOptionalStringField(input, field);
  if (!value) {
    return null;
  }
  if (value === "wakeloop_assistant") {
    return value;
  }
  throw new Error(`invalid profile payload: unsupported ${field}`);
}

function readRecordField(
  input: Record<string, unknown>,
  field: string
): Record<string, unknown> {
  const value = input[field];
  if (isRecord(value)) {
    return value;
  }
  throw new Error(`invalid api payload: missing ${field}`);
}

function readArrayField(
  input: Record<string, unknown>,
  field: string
): Record<string, unknown>[] {
  const value = input[field];
  if (!Array.isArray(value)) {
    throw new Error(`invalid api payload: missing ${field}`);
  }

  return value.filter(isRecord);
}

function readRequiredStringField(
  input: Record<string, unknown>,
  field: string
): string {
  const value = normalizeOptionalString(input[field]);
  if (!value) {
    throw new Error(`invalid profile payload: missing ${field}`);
  }
  return value;
}

function readOptionalStringField(
  input: Record<string, unknown>,
  field: string
): string | null {
  return normalizeOptionalString(input[field]);
}

function readBooleanField(
  input: Record<string, unknown>,
  field: string
): boolean {
  return input[field] === true;
}

function readOptionalBooleanField(
  input: Record<string, unknown>,
  field: string
): boolean | null {
  const value = input[field];
  if (typeof value === "boolean") {
    return value;
  }
  return null;
}

function normalizeOptionalString(value: unknown): string | null {
  const normalized = String(value ?? "").trim();
  return normalized.length > 0 ? normalized : null;
}

function normalizeTokenType(tokenType: string): string {
  const normalized = tokenType.trim();
  return normalized.length > 0 ? normalized : "Bearer";
}

function toAuthEndpoint(baseUrl: string, pathname: string): string {
  const url = new URL(baseUrl);
  url.pathname = pathname;
  url.search = "";
  url.hash = "";
  return url.toString();
}

async function parseResponseJson(response: Response): Promise<unknown> {
  const raw = await response.text();
  if (!raw) {
    return null;
  }
  try {
    return JSON.parse(raw);
  } catch {
    return { message: raw };
  }
}

function extractApiErrorMessage(payload: unknown, status: number): string {
  const record = isRecord(payload) ? payload : {};
  const message =
    normalizeOptionalString(record.message) ??
    normalizeOptionalString(record.error_description) ??
    normalizeOptionalString(record.error);

  if (message) {
    return message;
  }

  return `request failed with status ${status}`;
}

function resolveProfilesApiErrorMessage(input: {
  payload: unknown;
  status: number;
  code: string | null;
  endpoint: string;
}): string {
  const message = extractApiErrorMessage(input.payload, input.status);
  if (
    input.status === 500 &&
    input.code === "profile.internal_error" &&
    isLoopbackProfileEndpoint(input.endpoint)
  ) {
    return (
      "internal error from local auth service; if profile schema changed, run " +
      "`pnpm db:migrate:dev` and retry."
    );
  }
  if (
    input.status === 500 &&
    input.code === "profile.internal_error" &&
    isHostedProfilesEndpoint(input.endpoint)
  ) {
    return (
      "internal error from WakeLoop auth service while handling profile state. " +
      "If this works in `wakeloop-dev` but fails in `wakeloop`, the hosted auth deployment " +
      "is likely out of sync with the latest profile flow. Retry after the " +
      "production auth deploy/migration is updated."
    );
  }

  return message;
}

function normalizeProfileLookupKey(value: string): string {
  return value.trim().replace(PROFILE_HANDLE_PREFIX_RE, "").toLowerCase();
}

async function findWakeLoopProfileByNameOrHandle(input: {
  profileIdentifier: string;
  profileKind?: WakeLoopProfileKind;
}): Promise<
  | { status: "found"; profile: WakeLoopProfile }
  | { status: "ambiguous" }
  | { status: "not_found" }
> {
  const lookupKey = normalizeProfileLookupKey(input.profileIdentifier);
  const matches = (await listWakeLoopProfiles()).filter((profile) => {
    if (input.profileKind && profile.profileKind !== input.profileKind) {
      return false;
    }
    return (
      normalizeProfileLookupKey(profile.profileName) === lookupKey ||
      normalizeProfileLookupKey(profile.profileHandle) === lookupKey
    );
  });

  if (matches.length === 1) {
    const profile = matches[0];
    if (profile) {
      return { status: "found", profile };
    }
  }
  if (matches.length > 1) {
    return { status: "ambiguous" };
  }
  return { status: "not_found" };
}

function isLoopbackProfileEndpoint(endpoint: string): boolean {
  const url = parseUrlOrNull(endpoint);
  return Boolean(
    url &&
      isLoopbackHostname(url.hostname) &&
      url.pathname.startsWith("/v1/profiles")
  );
}

function isHostedProfilesEndpoint(endpoint: string): boolean {
  const url = parseUrlOrNull(endpoint);
  return Boolean(
    url &&
      !isLoopbackHostname(url.hostname) &&
      url.pathname.startsWith("/v1/profiles")
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === "object" && !Array.isArray(value));
}
